import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { Divider } from 'antd';
import { AtsSkeleton } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import type { InterviewFeedbackGroup } from 'constants/interview';
import type { RootState } from 'store';
import { getUser } from 'store/selectors/authorizeSelectors';
import { getInterviewFeedbackGroups, getInterviewFeedbacksLoading } from 'store/selectors/jobSelectors';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import { FeedbackGroup } from './FeedbackGroup';
import { useInterviewFeedbackActions } from './useInterviewFeedbackActions';
import { getFeedbackSectionTitle } from './utils';
import type { InterviewFeedbackProps } from './types';
import './style.scss';

const EMPTY_GROUPS: InterviewFeedbackGroup[] = [];
const selectEmptyGroups = (_state: RootState) => EMPTY_GROUPS;

export const InterviewFeedback: React.FC<InterviewFeedbackProps> = ({
  defaultOpen,
  interviewKind,
  onFeedbackChanged,
  sourceStepName,
  stepId,
  interviewId,
  group,
  groupsCount: groupsCountProp,
  readOnly = false,
  embedded = false,
}) => {
  const {
    id: jobId,
    step: currentStepId,
    candidate: candidateId,
  } = useParams<{
    id: string;
    step: string;
    candidate: string;
  }>();
  const currentUser = useSelector(getUser);
  // Embedded cards receive a pre-built group from Interview; history sections group once via selector.
  const stepGroups = useSelector(group ? selectEmptyGroups : getInterviewFeedbackGroups(stepId));
  const loading = useSelector(getInterviewFeedbacksLoading(stepId));
  const title = getFeedbackSectionTitle(interviewKind, sourceStepName);

  const [openDetails, setOpenDetails] = useState(defaultOpen);
  const [showAllFeedbacks, setShowAllFeedbacks] = useState(false);
  const [editUuid, setEditUuid] = useState('');

  const groups = group ? [group] : stepGroups;
  const groupsCount = groupsCountProp ?? groups.length;

  const { onImproveComment, onPostFeedback, onSaveScorecard } = useInterviewFeedbackActions({
    candidateId,
    interviewId: interviewId || group?.interviewId,
    jobId,
    onFeedbackChanged,
    setEditUuid,
    stepId,
  });

  const groupProps = {
    currentStepId,
    currentUserUuid: currentUser?.uuid,
    editUuid,
    embedded,
    groupsCount,
    onEdit: setEditUuid,
    onEditCancel: () => setEditUuid(''),
    onFinish: onSaveScorecard,
    onImproveComment,
    onPostFeedback,
    onToggleDetails: () => setOpenDetails(open => !open),
    onToggleShowAll: () => setShowAllFeedbacks(open => !open),
    openDetails,
    readOnly,
    showAllFeedbacks,
    stepId,
  };

  if (loading && !embedded) {
    return (
      <div className="job-interview-feedback">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{title}</Typography.Title2>
        <AtsSkeleton height={36} />
        <Divider />
      </div>
    );
  }

  const hasContent = groups.some(item => item.feedbacks.length) || (embedded && (interviewId || group));
  if (!hasContent) return null;

  if (embedded) {
    return (
      <div className="job-interview-feedback job-interview-feedback--embedded">
        {groups.map(item => (
          <FeedbackGroup key={item.interviewId || item.sequence} group={item} showRoundTitle={false} {...groupProps} />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('job-interview-feedback', { open: openDetails })}>
      <div className="job-interview-feedback__header">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{title}</Typography.Title2>
        <Arrow className="open-details" onClick={() => setOpenDetails(open => !open)} />
      </div>
      <div className="job-interview-feedback__content">
        {groups.map(item => (
          <FeedbackGroup
            key={item.interviewId || item.sequence}
            group={item}
            showRoundTitle={groupsCount > 1}
            {...groupProps}
          />
        ))}
      </div>
      <Divider />
    </div>
  );
};
